import { Fragment, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  productRankApi,
  type ProductRankParams,
  type ProductRankRow,
  type ProductRankSortField,
  type ProductRankViewType,
} from '../../api/productRank.js';
import { ErrorBox, Field, Pagination, Spinner } from '../../components/ui.js';

/**
 * 상품 랭킹 순위 — 원본 xadmin/marketing/product/list.php.
 *
 * 원본 화면이 하던 일
 *  - 월별/주차별 보기. 보기를 바꾸면 기준일자를 비우고 바로 다시 조회한다.
 *  - 기준일자(월별은 달, 주차별은 날짜)를 고르면 바로 조회한다.
 *  - 상품코드는 Enter 나 '검색' 으로 조회한다.
 *  - 클릭 Total / 주문 Total 머리칸으로 정렬. 같은 칸을 다시 누르면 방향만 뒤집고,
 *    다른 칸으로 옮기면 내림차순부터.
 *  - 한 쪽 20행을 기간별로 묶어, 묶음마다 이 쪽에 보이는 행의 클릭·주문 합계를 낸다.
 *  - 순위는 기간과 상관없이 쪽을 넘어 이어 센다 ((쪽-1)×20 + 몇 번째).
 *  - CSV 는 지금 조건 그대로 전부 (쪽 나눔 없이).
 *
 * 조건은 URL 이 갖는다 - 원본처럼 주소만 복사하면 같은 화면이 열린다.
 */

const VIEW_LABEL: Record<ProductRankViewType, string> = { month: '월별', week: '주차별' };

function paramsFrom(sp: URLSearchParams): ProductRankParams {
  return {
    view_type: sp.get('view_type') === 'week' ? 'week' : 'month',
    search_date: sp.get('search_date') ?? '',
    product_code: sp.get('product_code') ?? '',
    sort_field: sp.get('sort_field') === 'totalorder' ? 'totalorder' : 'total',
    sort_dir: (sp.get('sort_dir') ?? '').toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
    page: Math.max(1, Number.parseInt(sp.get('page') ?? '', 10) || 1),
  };
}

/** 원본 formatNumber - 값이 없으면 0. */
const fmt = (n: number | null | undefined) => (n ? n.toLocaleString() : '0');

interface PeriodGroup {
  period: string;
  rows: (ProductRankRow & { rank: number })[];
  total: number;
  totalorder: number;
}

function groupByPeriod(items: ProductRankRow[], offset: number): PeriodGroup[] {
  const groups = new Map<string, PeriodGroup>();
  items.forEach((row, i) => {
    let g = groups.get(row.period);
    if (!g) {
      g = { period: row.period, rows: [], total: 0, totalorder: 0 };
      groups.set(row.period, g);
    }
    g.rows.push({ ...row, rank: offset + i + 1 });
    g.total += row.total;
    g.totalorder += row.totalorder;
  });
  return [...groups.values()];
}

export function ProductRankPage() {
  const [sp, setSp] = useSearchParams();
  const params = paramsFrom(sp);
  // 상품코드는 치는 동안은 화면에만 두고, 조회할 때 URL 로 올린다.
  const [codeDraft, setCodeDraft] = useState<string | null>(null);
  const productCode = codeDraft ?? params.product_code;

  const list = useQuery({
    queryKey: ['product-rank', params],
    queryFn: () => productRankApi.list(params),
    placeholderData: keepPreviousData,
  });

  /** 원본 executeSearch - 지금 화면의 조건(치던 상품코드 포함)으로 다시 연다. */
  function go(patch: Partial<ProductRankParams>) {
    const next = { ...params, product_code: productCode, ...patch };
    const out = new URLSearchParams();
    if (next.view_type !== 'month') out.set('view_type', next.view_type);
    if (next.search_date) out.set('search_date', next.search_date);
    if (next.product_code.trim()) out.set('product_code', next.product_code.trim());
    if (next.sort_field !== 'total') out.set('sort_field', next.sort_field);
    if (next.sort_dir !== 'DESC') out.set('sort_dir', next.sort_dir);
    if (next.page > 1) out.set('page', String(next.page));
    setCodeDraft(null);
    setSp(out);
  }

  function toggleSort(field: ProductRankSortField) {
    const dir = params.sort_field === field ? (params.sort_dir === 'DESC' ? 'ASC' : 'DESC') : 'DESC';
    go({ sort_field: field, sort_dir: dir, page: 1 });
  }

  function reset() {
    setCodeDraft(null);
    setSp(new URLSearchParams());
  }

  const data = list.data;
  const groups = useMemo(
    () => (data ? groupByPeriod(data.items, (params.page - 1) * data.perPage) : []),
    [data, params.page],
  );
  const viewLabel = VIEW_LABEL[params.view_type];
  // 서버가 형식이 틀린 기준일자를 버렸으면 입력칸도 비운다 (원본도 버린 값을 다시 그렸다).
  const shownDate = data && data.filters.viewType === params.view_type ? data.filters.searchDate : params.search_date;

  const sortMark = (field: ProductRankSortField) =>
    params.sort_field === field ? (params.sort_dir === 'DESC' ? '▼' : '▲') : '▼';

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            <button type="button" className="rank-title" onClick={reset}>
              상품 랭킹 순위 (클릭 및 주문)
            </button>
          </h1>
          <p className="page-desc">상품별 클릭(1인당 1일 1클릭)과 주문수를 월별·주차별로 묶어 순위를 매깁니다.</p>
        </div>
        <div className="rank-badge">
          전체 내역: <strong>{fmt(data?.total)}</strong>건
        </div>
      </header>

      <div className="filters">
        <Field label="보기">
          <select
            className="input"
            value={params.view_type}
            onChange={(e) => go({ view_type: e.target.value as ProductRankViewType, search_date: '', page: 1 })}
          >
            <option value="month">월별</option>
            <option value="week">주차별</option>
          </select>
        </Field>
        <Field label="기준일자">
          <input
            key={params.view_type}
            className="input"
            type={params.view_type === 'month' ? 'month' : 'date'}
            value={shownDate}
            onChange={(e) => go({ search_date: e.target.value, page: 1 })}
          />
        </Field>
        <Field label="상품코드">
          <input
            className="input"
            value={productCode}
            placeholder="상품코드 검색"
            onChange={(e) => setCodeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                go({ page: 1 });
              }
            }}
          />
        </Field>
        <button type="button" className="btn" onClick={() => go({ page: 1 })}>
          검색
        </button>
        <button type="button" className="btn btn--ghost" onClick={reset}>
          ↺ 초기화
        </button>
        <a
          className="btn btn--primary filters__reset"
          href={productRankApi.csvUrl({ ...params, product_code: productCode.trim() })}
        >
          ⬇ CSV 다운로드
        </a>
      </div>

      {list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : !data ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <div className="rank-summary">
            <div>
              <div className="rank-summary__title">
                <span>{viewLabel}</span> 기간 그룹
              </div>
              <div className="rank-summary__sub">
                조회 결과 {fmt(data.total)}건 · 페이지당 {fmt(data.perPage)}건
              </div>
            </div>
            <div className="rank-summary__pill">현재 페이지 기간 {fmt(groups.length)}개</div>
          </div>
          <table className="table rank-table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ textAlign: 'center', width: 60 }}>순위</th>
                <th rowSpan={2} style={{ width: '12%' }}>조회 기간</th>
                <th rowSpan={2}>상품명(코드)</th>
                <th colSpan={3} style={{ textAlign: 'center' }}>클릭(1인당 1일 1클릭으로 집계)</th>
                <th colSpan={3} style={{ textAlign: 'center' }}>주문수(건)</th>
              </tr>
              <tr>
                <th style={{ textAlign: 'right' }}>PC</th>
                <th style={{ textAlign: 'right' }}>Mobile</th>
                <th style={{ textAlign: 'right' }} aria-sort={ariaSort(params, 'total')}>
                  <button type="button" className="th-sort" title="트래픽 총합순 정렬" onClick={() => toggleSort('total')}>
                    Total <span className={sortClass(params, 'total')}>{sortMark('total')}</span>
                  </button>
                </th>
                <th style={{ textAlign: 'right' }}>PC</th>
                <th style={{ textAlign: 'right' }}>Mobile</th>
                <th style={{ textAlign: 'right' }} aria-sort={ariaSort(params, 'totalorder')}>
                  <button type="button" className="th-sort" title="주문수 총합순 정렬" onClick={() => toggleSort('totalorder')}>
                    Total <span className={sortClass(params, 'totalorder')}>{sortMark('totalorder')}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.period}>
                  <tr className="rank-period">
                    <td colSpan={9}>
                      <div className="rank-period__head">
                        <div>
                          <div className="rank-period__name">{g.period}</div>
                          <div className="rank-period__meta">
                            {viewLabel} · {fmt(g.rows.length)}개 상품
                          </div>
                        </div>
                        <div className="rank-period__totals">
                          <span>
                            클릭 합계 <strong>{fmt(g.total)}</strong>
                          </span>
                          <span className="rank-period__order">
                            주문 합계 <strong>{fmt(g.totalorder)}</strong>
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  {g.rows.map((row) => (
                    <tr key={`${row.period}_${row.product}`}>
                      <td className="rank-rank">{row.rank}</td>
                      <td>
                        <span className="rank-period__label">{row.period}</span>
                      </td>
                      <td>
                        {row.gname || '이름 없음'}
                        <span className="rank-code">({row.product})</span>
                      </td>
                      <td className="num rank-num--pc">{fmt(row.p)}</td>
                      <td className="num rank-num--mobile">{fmt(row.m)}</td>
                      <td className="num rank-num--total">{fmt(row.total)}</td>
                      <td className="num rank-num--order">{fmt(row.porder)}</td>
                      <td className="num rank-num--order">{fmt(row.morder)}</td>
                      <td className="num rank-num--order rank-num--strong">{fmt(row.totalorder)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={9} className="rank-empty">
                    조회된 데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pagination
            page={params.page}
            totalPages={data.totalPages}
            total={data.total}
            onChange={(page) => go({ page })}
          />
        </div>
      )}
    </section>
  );
}

function ariaSort(p: ProductRankParams, field: ProductRankSortField) {
  if (p.sort_field !== field) return 'none';
  return p.sort_dir === 'ASC' ? 'ascending' : 'descending';
}

function sortClass(p: ProductRankParams, field: ProductRankSortField) {
  return p.sort_field === field ? 'th-sort__arrow th-sort__arrow--on' : 'th-sort__arrow';
}
